import { useNavigate } from "react-router-dom";
import { RotateCcw } from "lucide-react";
import { WelcomeFrame } from "@/components/voice-onboarding/WelcomeFrame";
import { useOnboardingBuild } from "@/hooks/voice-onboarding/useOnboardingBuild";
import { BuildingRows } from "./BuildingRows";
import { ONBOARD_ROUTES, VOICE_ONBOARD_STEPS } from "./steps";

/**
 * Step 4 of 7 — Build the brain, for real.
 *
 * This step creates the actual Genie and reads the owner's website into its
 * knowledge. It used to run a scripted checklist on timers with no backend
 * behind it, which meant the flow whose whole job was "you built a Genie
 * before you opened the app" built nothing.
 *
 * The CTA only enables when a real Genie exists. If creation fails the owner
 * is told plainly and can retry — they are never walked forward into a flow
 * that has nothing behind it.
 */
const BuildingPage = () => {
  const navigate = useNavigate();
  const { stages, result, fatalError, isComplete, retry } = useOnboardingBuild();

  const knowledgeFailed = result?.knowledge.attempted && !result.knowledge.ok;

  const footerNote = fatalError
    ? "Nothing was saved, so there is nothing to undo."
    : isComplete
      ? knowledgeFailed
        ? "Your genie is ready. You can add its knowledge in a moment."
        : "Your genie is ready to meet you."
      : "This takes a moment. We are doing the real work now.";

  return (
    <WelcomeFrame
      step={4}
      steps={VOICE_ONBOARD_STEPS}
      eyebrow="ONE MOMENT"
      title={fatalError ? "We could not build your genie" : "Building your genie's brain"}
      subtitle={
        fatalError
          ? fatalError
          : "Turning your details into a working genie. This is the part that usually takes a team a week."
      }
      showBack={false}
      showSkip={false}
      continueLabel="Meet my genie"
      continueDisabled={!isComplete}
      footerNote={footerNote}
      maxWidth={520}
      onContinue={() => navigate(ONBOARD_ROUTES[4])}
    >
      <div className="w-full flex flex-col gap-4">
        <BuildingRows stages={stages} />

        {fatalError && (
          <button
            type="button"
            onClick={retry}
            className="self-center inline-flex items-center gap-1.5 text-[13px] font-semibold text-genie-primary hover:opacity-80 transition-opacity"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Try again
          </button>
        )}
      </div>
    </WelcomeFrame>
  );
};

export default BuildingPage;
